import React, { MouseEventHandler, Ref, useEffect, useMemo, useRef, useState } from 'react';
import styles from './chat-viewport.module.scss';
import msgStyles from './components/chat-message-item/chat-message-item.module.scss';
import { Container, Typography } from '@/ui-kit';
import { jwtService } from '@/services/jwt.service';
import { MessageDto, PaginationDto } from '@/types/message';
import { differenceInMinutes, format, parse } from 'date-fns';
import * as locales from 'date-fns/locale';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ErrorMessage } from '../error-message';
import { SkeletonChatViewport } from './components/skeleton-chat-viewport';
import { BadgeDay } from './components/badge-day';
import { ChatMessageItem } from './components/chat-message-item';
import { FormMessage } from './components/form-message';
import { useActionCableCreate } from '@/hooks/use-action-cable';
import { eventsApi, profilesApi } from '@/api';
import { PulseLoader } from 'react-spinners';
import WebApp from '@twa-dev/sdk';
import { useNavigate } from 'react-router-dom';
import { routesApp } from '@/constans/routes';
import { Button } from '@/ui-kit';
import { ChatMode, EDIT_MODE, REPLY_MODE, Suggestion, useChat, type RecipientParams } from '@/store/useChat';
import { useAuth } from '@/store/useAuth';
import { OTHER_INACTIVE } from '@/utils/socker-response-errors';
import { UAParser } from 'ua-parser-js';
import { SocketPayload } from '@/types/socket-payload';
import { useToast } from '@/store/useToast';
import { ErrorNotificationIcon, InfoIcon, SuccessNotificationIcon, VerificationIcon, VerifiedAccountIcon } from '@/assets/icons';
import { ChatIcon } from '@/assets/icons';
import { Profile } from '@/types/profile';
import { AxiosError, AxiosResponse } from 'axios';
import clsx from 'clsx';
import editIcon from '@/assets/icons/edit-icon.svg';
import replyIcon from '@/assets/icons/reply-icon.svg';
import { useVerification } from '@/store/useVerification';
import { isRecentMsg } from '@/utils/chat';
import { useTranslation } from 'react-i18next';
import { useLogProfileChannelRejection } from '@/hooks/use-log-profile-channel-rejection';
import { localizeDate } from '@/utils/msgs';

type SendMsgPayload = {
    chat_id: number | string,
    message: {
        reply_message_id: number | null,
        text: string,
    },
    ice_breaker_used?: boolean,
    suggestions_count?: number,
    suggestion_id?: number,
}

interface Props {
    chatUrl?: string;
    isLoadingChat?: boolean;
    isErrorChat?: boolean;
    messagesData?: MessageDto[];
    paginationData?: PaginationDto;
    chatId: number | string;
    profileId: number | string | undefined;
    profileChat: Profile | undefined;
    expiredAt?: string | null,
    isChatInit: boolean,
    refetchMessages: () => void;
    onOtherProfileVerificationStart: (profile: Profile) => void;
    onOtherProfileVerificated: (profile: Profile) => void;
}

type GroupedMessage =
    | {
          type: 'date';
          id: string;
          date: string | null;
      }
    | {
          type: 'data';
          id: number;
          created_at: string;
          is_read: boolean;
          other_id: number;
          profile_id: number;
          text: string;
          date?: string;
          edited_at: string | null;
          reply_message_id?: number;
          reply_message_text?: string;
          has_reply?: boolean;
          unsafe_message?: boolean;
      };

const CHANEL_NAME = 'ChatChannel';
const isDev = process.env.NODE_ENV === 'development';
const getPathFromWebSocketUrlIsDev = (url: string) => {
    if (isDev) {
        // const parsedUrl = new URL(url);
        // return parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;
        // TODO: url to env
        return 'http://localhost:8255/cable';
        // return 'http://localhost:3300/cable';
    }
    return url;
};
let chanel: any;

export const ChatViewport: React.FC<Props> = ({
    chatUrl = '',
    isLoadingChat,
    isErrorChat,
    messagesData,
    paginationData,
    chatId,
    profileId,
    profileChat,
    expiredAt,
    isChatInit,
    refetchMessages,
    onOtherProfileVerificationStart,
    onOtherProfileVerificated,
}) => {
    const messageWrap = useRef<HTMLDivElement>(null);
    const { messages, setMessages } = useChat(state => state);
    const menuRef = useRef<HTMLDialogElement>(null)
    const [pagination, setPagination] = useState<PaginationDto | null>(null);
    const [isFirstRender, setIsFirstRender] = useState(false);
    const [hasMoreMessages, setHasMoreMessages] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [maxChatHeight, setMaxChatHeight] = useState(0);
    const navigate = useNavigate();
    const userData = useAuth((state) => state.getUser());
    const { setHasPendingVerificationError } = useVerification((state) => state);
    const [iceBreakerUsed, setIceBreakerUsed] = useState(false)

    const cable = useActionCableCreate(getPathFromWebSocketUrlIsDev(chatUrl));

    const showToast = useToast((state) => state.show);

    const { t, i18n } = useTranslation();

    const { logProfileChannelRejection } = useLogProfileChannelRejection()

    useEffect(() => {
        chanel = cable.subscriptions.create(
            {
                channel: CHANEL_NAME,
                authentication: WebApp.initData.length ? WebApp.initData : jwtService.getAccessToken(),
                id: chatId,
            },

            {
                connected() {
                    console.log('conented');
                },
                received(payload: SocketPayload) {
                    console.log(`received: ${JSON.stringify(payload)}`)
                    if (payload.error) {
                        if (payload.error === OTHER_INACTIVE)
                            showToast({
                                // type error
                                icon: <ErrorNotificationIcon />,
                                msg: t('msgs.user_temp_deactivated')
                            });
                        else
                            navigate(routesApp.banned);
                        return;
                    }

                    if (payload.type === 'profile_under_verification') {
                        if (payload.data.profile.id != profileChat?.id) return;
                        onOtherProfileVerificationStart?.(payload.data.profile)
                        return
                    }

                    if (payload.type === 'other_profile_verified') {
                        if (payload.data.chat_id != chatId) return
                        if (payload.data.profile.id != profileChat?.id) return;
                        onOtherProfileVerificated?.(payload.data.profile)
                        return
                    }

                    const data: MessageDto = payload.data
                    setMessages((prevMessages) => {
                        // Проверяем, есть ли сообщение с таким ID в чате
                        const messageExists =
                            prevMessages.some(
                                (msg) =>
                                    msg.id === data.id ||
                                    msg.id === data.message_id
                            );
                        const newMessages = messageExists
                            ? prevMessages.map(
                                (msg) =>
                                    (
                                        msg.id === data.id ?
                                            data
                                        : msg.id === data.message_id ?
                                            {
                                                ...msg,
                                                ...data
                                            }
                                        :
                                            msg
                                    )
                            ) // Если сообщение существует - обновляем его
                            : [...prevMessages, data]; // Если сообщение новое - добавляем в конец

                        setTimeout(() => {
                            window.scrollTo({
                                top: document.documentElement.scrollHeight,
                                behavior: 'smooth',
                            });
                        }, 0);

                        return newMessages;
                    });
                },
                disconnected() {
                    console.log('WebSocket отключён. Пытаемся переподключиться...');
                },
                rejected() {
                    console.log('Сервер отклонил подписку!');
                    logProfileChannelRejection({ channel: CHANEL_NAME, chat: chatId })
                },
                update_message(data: {
                    message_id: number | string,
                    message: string,
                }) {
                    this.perform('update_message', data);
                },
            },
        );
        return () => {
            cable.subscriptions.remove(chanel);
            // setIsFirstRender(false);
            // setContextMsg(null);
            //clearMenu();
        };
    }, []);

    useEffect(() => {
        setHasMoreMessages(pagination?.next !== null);
    }, [pagination]);

    useEffect(() => {
        if (paginationData && !isFirstRender) {
            console.log('paginationData', paginationData);
            setPagination(paginationData);
        }

        if (messagesData) {
            setMessages((prev) => {
                const newMessages = messagesData.filter(
                    (newMsg) => !prev.some((existingMsg) => existingMsg.id === newMsg.id),
                );
                return [...prev, ...newMessages];
            });
            setIsFirstRender(true);
        }
    }, [messagesData, paginationData]);

    useEffect(() => {
        if (paginationData && isFirstRender) {
            setPagination(paginationData);
        }
    }, [paginationData]);

    useEffect(() => {
        if (
            isFirstRender &&
            messages &&
            messages.length < 10 &&
            !isLoading &&
            !isErrorChat
        ) loadMoreMessages();
    }, [messages, isFirstRender, isLoading]);

    // @ts-ignore
    const groupedMessages = useMemo<GroupedMessage[]>(() => {
        if (!messages.length) return [];

        // 1. Сортируем сообщения по дате (новые → старые)
        const sortedMessages = [...messages].sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        );

        const result = [];
        let currentDate: string | null = null;

        // 2. Группируем сообщения по дате и добавляем дату ПОСЛЕ всех сообщений
        for (const message of sortedMessages) {
            const messageDateStr = new Date(message.created_at).toISOString().split('T')[0];

            // Если дата изменилась, добавляем дату ПЕРЕД переходом на новую дату
            if (currentDate !== null && currentDate !== messageDateStr) {
                result.push({
                    type: 'date',
                    id: `date-${currentDate}`, // Уникальный ID на основе даты
                    date: currentDate,
                });
            }

            // Добавляем сообщение
            result.push({
                type: 'data',
                date: currentDate,
                ...message,
            });

            currentDate = messageDateStr;
        }

        // Добавляем последнюю дату (т.к. цикл её не добавил)
        if (currentDate !== null) {
            result.push({
                type: 'date',
                id: `date-${currentDate}`, // Уникальный ID для последней даты
                date: currentDate,
            });
        }

        return result;
    }, [messages]);

    const generateSuggestion = useChat((state) => state.generateSuggestion);

    const handleViewportChange = () => {
        if (window.visualViewport) {
            console.log('start handleViewportChange chat', window.visualViewport.height, WebApp.platform);

            setMaxChatHeight(window.visualViewport.height);

            setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }, 800);

            WebApp.expand();
        }
    };

    useEffect(() => {
        if (window.visualViewport) {
            setMaxChatHeight(window.visualViewport.height);

            window.visualViewport.addEventListener('resize', handleViewportChange);
        }

        return () => {
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', handleViewportChange);
            }
            // setMaxChatHeight(0);
            setMessages(() => []);
            // setPagination(null);
            // setChatMode(null);
            //setMsg('');
            setMessages((_) => [])
        };
    }, []);

    const handleScrollToMessagesWrapperEnd = () => {
        if (messageWrap.current) {
            messageWrap.current.scrollTop = messageWrap.current.scrollHeight;
        }
    };

    const sendMessage = async (message: { message: string }) => {
        // chanel?.send(message);
        if (chatId) {
            setIsLoading(true)
            try {
                const resData: SendMsgPayload = {
                    chat_id: chatId,
                    message: {
                        reply_message_id: null,
                        text: message.message,
                    }
                }

                if (iceBreakerUsed)
                    resData.ice_breaker_used = true;

                if (suggestionCounter) {
                    resData.suggestions_count = suggestionCounter
                }
                if (lastSuggestionId) {
                    resData.suggestion_id = lastSuggestionId
                }

                let res = await profilesApi.sendMsg(resData);
                handleSendMsgError(res)
                setTimeout(handleScrollToMessagesWrapperEnd, 1000);
                setTimeout(() => {
                    window.scrollTo({ top: 0, behavior: 'auto' });
                }, 300);
                setSuggestionCounter(0)
                setLastSuggestionId(null)
            } catch (e) {
                if (
                    e instanceof AxiosError &&
                    e.response
                ) {
                    handleSendMsgError(e.response)
                }
            } finally {
                setIsLoading(false)
            }
        }
    };

    const handleUpdateMsgError = (e: AxiosResponse) => {
        if (e?.status === 422) {
            if (
                Object
                    .keys(e?.data)
                    .includes('other')
            ) {
                if (
                    e
                        .data
                        .other
                        .includes('other_inactive')
                ) {
                    navigate(routesApp.browse, {
                        replace: true
                    })
                    showToast({
                        // type error
                        icon: <ErrorNotificationIcon />,
                        msg: t('msgs.no_access_to_chat')
                    });
                }
            }
        } else if (e.status === 403) {
            if (e.data.code === 'bad_words')
                setHasPendingVerificationError(true)
        }
    }

    const handleSendMsgError = (e: AxiosResponse) => {
        if (e?.status === 403) {
            if (
                e
                    ?.data
                    ?.error
                === 'User deactivated'
            ) {
                navigate(routesApp.browse, {
                    replace: true
                })
                showToast({
                    // type error
                    icon: <ErrorNotificationIcon />,
                    msg: t('msgs.no_access_to_chat')
                });
            } else if (
                e
                    ?.data
                    ?.code
                === 'bad_words'
            ) {
                setHasPendingVerificationError(true)
            }
        }
    }

    const replyMsg = async ({
        reply_message_id: msgId,
        message: msgContent
    }: {
        reply_message_id: number | string
        message: string,
    }) => {
        setIsLoading(true)
        try {
            let res = await profilesApi.sendMsg({
                chat_id: chatId,
                message: {
                    reply_message_id: msgId,
                    text: msgContent
                }
            });
            handleSendMsgError(res)
            setTimeout(handleScrollToMessagesWrapperEnd, 1000);
            setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'auto' });
            }, 300);
        } catch (e) {
            if (
                e instanceof AxiosError &&
                e.response
            ) {
                handleSendMsgError(e.response)
            }
        } finally {
            setIsLoading(false)
        }
    }

    const getDateStr = () => {
        const now = new Date();
        const offset = -now.getTimezoneOffset() / 60;
        const sign = offset >= 0 ? '+' : '-';
        const pad = (num: number) => Math.abs(num).toString().padStart(2, '0');
        return now.toISOString().replace('Z', `${sign}${pad(offset)}:00`);
    };

    const LAST_MSG_TABLE = 'last-chat-msg'
    const LAST_CHAT_MODE_TABLE = 'last-chat-mode'
    const PK = 'id'
    
    const getTxLastMsgStore = (dbRequest: IDBOpenDBRequest) => {
        // Start a new transaction
        const db = dbRequest.result;
        const tx = db.transaction(LAST_MSG_TABLE, 'readwrite');
        return tx.objectStore(LAST_MSG_TABLE);
    }

    const getTxLastChatModeStore = (dbRequest: IDBOpenDBRequest) => {
        // Start a new transaction
        const db = dbRequest.result;
        const tx = db.transaction(LAST_CHAT_MODE_TABLE, 'readwrite');
        return tx.objectStore(LAST_CHAT_MODE_TABLE);
    }

    const clearMsgCache = () => {
        if (!dbRequest) return;
        getTxLastMsgStore(dbRequest).delete(chatId);
    }

    const clearChatModeCache = () => {
        if (!dbRequest) return;
        getTxLastChatModeStore(dbRequest).delete(chatId);
    }

    const handleChatModeClear = () => {
        clearMsgCache()
        clearChatModeCache()
    }

    const updateMsg = async ({
        message_id: msgId,
        message: msgContent
    }: {
        message_id: number | string
        message: string,
    }) => {
        setIsLoading(true)
        try {
            let res = await profilesApi.updateMsg(msgId, {
                text: msgContent,
                is_edited: true,
                edited_at: getDateStr(),
            });
            handleUpdateMsgError(res)
        } catch (e) {
            if (
                e instanceof AxiosError &&
                e.response
            ) {
                handleUpdateMsgError(e.response)
            }
        } finally {
            setIsLoading(false)
        }
    }

    const onFocusHandle = () => {
        // @ts-ignore
        // if (window.visualViewport && window['baseHeight']) {
        //     // @ts-ignore
        //     console.log('window[baseHeight]', window['baseHeight']);
        //     console.log('window.visualViewport.height', window.visualViewport.height);
        //     setMaxChatHeight(window.visualViewport.height);
        // }
    };

    const onBlurHandle = () => {
        // @ts-ignore
        //setMaxChatHeight(0);
    };

    const sendMessageRead = async (id: number | string) => {
        try {
            let res = await profilesApi.readMsg(id);
            if (res.status !== 200) {
                // TODO: handle error
            }
        } catch (e) {
            // TODO: handle error
        }
    };

    const loadMoreMessages: () => Promise<boolean | undefined | null> = async () => {
        try {
            console.log(chatId, pagination);
            if (chatId && pagination && pagination.next && !isLoading) {
                setIsLoading(true);
                // нужно переделать логику запроса под @tanstack/react-query
                let data = await profilesApi.getMessagesByIdNext(chatId, pagination.next);

                if (data.data.data) {
                    setMessages((prev) => {
                        const newMessages = data.data.data.filter(
                            (newMsg) => !prev.some((existingMsg) => existingMsg.id === newMsg.id),
                        );
                        return [...prev, ...newMessages];
                    });

                    setPagination(data.data.pagination);
                    console.log('new pagination');
                    return data.data.pagination.next !== null
                }
            }
        } catch (error) {
            console.error('Error loading more messages:', error);
        } finally {
            setIsLoading(false);
            setIceBreakerUsed(false);
        }
    };

    
    const onSuggestionGenerated = (payload: RecipientParams) => {
        setIceBreakerUsed(true)
        eventsApi.updateUserEvent(
            'button_ledokol',
            payload
        );
    }

    const [suggestionCounter, setSuggestionCounter] = useState(0)
    const [lastSuggestionId, setLastSuggestionId] = useState<number | null>(null)
    
    const onSuggestionGeneratedSuccess = (suggestion: Suggestion) => {
        showToast({
            icon: <SuccessNotificationIcon />,
            msg: t('msgs.phrase_generated'),
        })
        setSuggestionCounter((e) => e + 1)
        setLastSuggestionId(suggestion.id)
        if (!dbRequest) return;
        getTxLastMsgStore(dbRequest)
            .put({ [PK]: chatId, msg: suggestion.phrase });
    }

    const onSuggestionGeneratedFail = () =>
        showToast({
            icon: <ErrorNotificationIcon />,
            msg: t('error_try_again')
        })

    const fadeOut = [{ opacity: 1 }, { opacity: 0 }]
    const fadeIn = [{ opacity: 0 }, { opacity: 1 }]
    const fast = {duration: 250, fill: 'both'} as KeyframeAnimationOptions

    const onHintWhatToWriteAboutClick = async (event: any) => {
        const params: RecipientParams = {
            recipient_id: profileId
        }
        const suggestionBtn = event.currentTarget as HTMLElement

        setGenerateSuggestionBtnClicked(true)
        await suggestionBtn.animate(fadeOut, fast).finished

        await generateSuggestion(
            params,
            onSuggestionGeneratedSuccess,
            onSuggestionGeneratedFail,
        )
        
        setSuggestionGenerated(true)
        await suggestionBtn.animate(fadeIn, fast).finished
        setGenerateSuggestionBtnClicked(false)
        onSuggestionGenerated(params)
    }

    const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);
    const contextMsg = useChat((state) => state.contextMsg);
    const chatMode = useChat((state) => state.chatMode);
    const setChatMode = useChat((state) => state.setChatMode);
    const isIncome = !(contextMsg?.is_mine ?? false)
    const isRecent = isRecentMsg(contextMsg)
    const isCanEdit =
        contextMsg?.is_mine &&
        !contextMsg?.has_reply &&
        isRecent
    const setMsg = useChat((state) => state.setMsg);
    const menu = useChat((state) => state.menu);
    const clearMenu = useChat((state) => state.clearMenu);
    const setContextMsg = useChat((state) => state.setContextMsg);
    useEffect(() => {

        if (menu !== null && !chatMode && menuRef)
            menuRef
                .current
                ?.showModal()
        else if (menu === null && menuRef)
            menuRef
                .current
                ?.close()
    }, [menu])
    const parser = new UAParser();
    const device = parser.getDevice();

    const handleClickOutside = (e: Event) => {
        const el = e.target as HTMLElement
        if (el.classList.contains(styles.menu)) return
        const isMyMessage = contextMsg?.is_mine
        const hasNotReply = !contextMsg?.has_reply
        const isCanEdit =
            isMyMessage &&
            isRecent &&
            hasNotReply
        const isIncome = !(contextMsg?.is_mine ?? false)
        const isHaveMenuItems =
            isCanEdit ||
            isIncome
        const isClear =
            !el.closest(`.${msgStyles.message}`) ||
            !isHaveMenuItems
        if (isClear) {
            clearMenu()
            if (chatMode === null)
                setContextMsg(null)
        }
    }

    useEffect(() => {
        if (device.type === 'mobile')
            document.addEventListener('touchend', handleClickOutside);
        else
            document.addEventListener('click', handleClickOutside);
        return () => {
            if (device.type === 'mobile')
                document.removeEventListener('touchend', handleClickOutside);
            else
                document.removeEventListener('click', handleClickOutside);
        };
    }, [menu, contextMsg, chatMode]);

    const isMale = userData?.gender === 'male'

    const [isGenerateSuggestionBtnClicked, setGenerateSuggestionBtnClicked] = useState(false)
    const [isSuggestionGenerated, setSuggestionGenerated] = useState(false)

    const [dbRequest, setDbRequest] = useState<IDBOpenDBRequest | null>(null)

    useEffect(() => {
        if (isChatInit) {
            const dbRequest = indexedDB.open('euphora-chat-detail', 2)
            setDbRequest(dbRequest)
            dbRequest.onupgradeneeded = function() {
                const stores = dbRequest.result.objectStoreNames
                if (!stores.contains(LAST_MSG_TABLE))
                    dbRequest.result.createObjectStore(LAST_MSG_TABLE, { keyPath: PK });
                if (!stores.contains(LAST_CHAT_MODE_TABLE))
                    dbRequest.result.createObjectStore(LAST_CHAT_MODE_TABLE, { keyPath: PK });
            };
            dbRequest.onsuccess = function() {
                if (!dbRequest) return;
                const reqLastMsg = getTxLastMsgStore(dbRequest).get(chatId);
                reqLastMsg.onsuccess = function() {
                    const lastMsg = reqLastMsg.result?.msg
                    if (lastMsg)
                        setMsg(lastMsg)
                };
                const reqLastChatMode =
                    getTxLastChatModeStore(dbRequest).get(chatId);
                reqLastChatMode.onsuccess = async function() {
                    const lastChatMode =
                        reqLastChatMode.result?.mode
                    if (lastChatMode) {
                        try {
                            const res = await profilesApi.getMsg(
                                reqLastChatMode.result?.msg
                            )
                            const msg = res.data
                            const isOutcome =
                                msg.profile_id === userData?.id
                            const isRecent = isRecentMsg(msg)
                            const isCanEdit =
                                isOutcome &&
                                !msg.has_reply &&
                                isRecent
                            if (isCanEdit || !isOutcome)
                                setLastChatMode(reqLastChatMode)
                        } catch (e) {
                            clearChatModeCache()
                        }
                    }
                };
            };
        }
    }, [isChatInit])

    const onMsgChanged = (msg: string) => {
        if (!dbRequest) return;
        getTxLastMsgStore(dbRequest).put({ [PK]: chatId, msg: msg });
    }

    const onChatModeUpdated = (id: string | number, mode: ChatMode) => {
        if (!dbRequest) return;
        getTxLastChatModeStore(dbRequest).put({
            [PK]: chatId,
            mode,
            msg: id,
        });
    }

    const onReplyPress = () => {
        if (contextMsg?.id) {
            setChatMode(REPLY_MODE)
            onChatModeUpdated(contextMsg?.id, REPLY_MODE)
        }
    }

    const onEditPress = () => {
        if (contextMsg) {
            const msg = contextMsg.content
            setMsg(msg)
            onMsgChanged(msg)
            if (contextMsg?.id) {
                setChatMode(EDIT_MODE)
                onChatModeUpdated(contextMsg?.id, EDIT_MODE)
            }
        }
    }

    const handleReplySwipe = (id: string | number) =>
        onChatModeUpdated(id, REPLY_MODE)

    const [lastChatMode, setLastChatMode] =
        useState<IDBRequest<{ msg: string | number, mode: ChatMode }> | null>(null);
    const [isLastChatModeLoaded, setisLastChatModeLoaded] =
        useState(false);

    useEffect(() => {
        if (
            lastChatMode &&
            !isLastChatModeLoaded
        ) {
            const msg =
                messages.find(
                    e =>
                        e.id === lastChatMode?.result.msg
                )
            if (msg) {
                msgRefs
                    .current[msg.id]
                    .scrollToRoot()
                setisLastChatModeLoaded(true)
                setChatMode(lastChatMode.result.mode)
                setContextMsg({
                    id: msg.id,
                    content: msg.text,
                    is_mine: msg.profile_id === userData?.id,
                    status: msg.is_read,
                    created_at: new Date(msg.created_at),
                    pos: {
                        top: null,
                        bottom: null
                    },
                    reply_message_id: msg?.reply_message_id ?? null,
                    has_reply: msg.has_reply,
                })
            } else if (pagination?.next) {
                loadMoreMessages()
            }
        }
    }, [lastChatMode, messages, isLastChatModeLoaded, pagination])

    const msgRefs =
        useRef<Record<string | number, { scrollToRoot: () => void }>>({})

    const isShowIcebreakerBtn = isMale && userData?.lang === 'ru'
    
    if (isErrorChat) {
        return <ErrorMessage message={t('msgs.accept_msgs_error')} />;
    }

    return (
        <div
            className={styles.wrapper}
            style={{
                maxHeight: maxChatHeight === 0 ? 'none' : maxChatHeight + 'px',
            }}>
            <Container className={styles.container}>
                <div
                    id="scrollableDiv"
                    ref={messageWrap}
                    className={
                        clsx(
                            styles.containerScroll,
                            groupedMessages.length === 0 && styles['containerScroll--empty']
                        )
                    }>
                    {
                        isIncome || isCanEdit ?
                            <>
                                {
                                    menu ?
                                        <div className={styles.menuBackdrop} />
                                    :
                                        <></>
                                }
                                <dialog
                                    ref={menuRef}
                                    className={styles.menuWrap}
                                    onTouchEnd={e => {
                                        if (
                                            device.type === 'mobile' &&
                                            menuRef.current?.open &&
                                            (e.target as HTMLElement).classList.contains(styles.menuWrap)
                                        ) {
                                            clearMenu()
                                            if (chatMode === null)
                                                setContextMsg(null)
                                        }
                                    }}
                                    onMouseUp={(e) => {
                                        if (
                                            device.type !== 'mobile' &&
                                            menuRef.current?.open &&
                                            (e.target as HTMLElement).classList.contains(styles.menuWrap)
                                        ) {
                                            clearMenu()
                                            if (chatMode === null)
                                                setContextMsg(null)
                                        }
                                    }}>
                                    <div
                                        className={styles.menu}
                                        style={{
                                            [contextMsg?.pos?.bottom ? 'bottom' : 'top']: `calc(${(contextMsg?.pos?.top ?? contextMsg?.pos?.bottom) ?? 0}px)`,
                                            ...(
                                                contextMsg?.is_mine ?
                                                    {
                                                        left: '15px'
                                                    }
                                                :
                                                    {
                                                        right: '15px'
                                                    }
                                            )
                                        }}>
                                        <ul className={styles.menuContent}>
                                            {
                                                isCanEdit ?
                                                    <li
                                                        className={styles.menuItem}
                                                        onClick={onEditPress}
                                                        onTouchEnd={onEditPress}>
                                                            <span>{t('msgs.edit')}</span>
                                                            <img
                                                                className={styles.menuItemIcon}
                                                                src={editIcon}
                                                                width="18.89px"
                                                                height="20px" />
                                                    </li>
                                                :
                                                    <></>
                                            }
                                            {
                                                isIncome ?
                                                    <li
                                                        className={styles.menuItem}
                                                        onTouchEnd={onReplyPress}
                                                        onClick={onReplyPress}>
                                                        <p className={styles.menuItemLabel}>{t('msgs.reply')}</p>
                                                        <img
                                                            className={styles.menuItemIcon}
                                                            src={replyIcon}
                                                            width="20px"
                                                            height="17.44px" />
                                                    </li>
                                                :
                                                    <></>
                                            }
                                        </ul>
                                    </div>
                                </dialog>
                            </>
                        :
                            <></>
                    }
                    {isLoadingChat ? (
                        <SkeletonChatViewport />
                    ) : groupedMessages.length > 0 ? (
                        <>
                            <InfiniteScroll
                                dataLength={groupedMessages.length}
                                next={loadMoreMessages}
                                hasMore={hasMoreMessages}
                                loader={
                                    <div className={styles.loader}>
                                        <PulseLoader size={12} color="#f26a1c" />
                                    </div>
                                }
                                style={{ display: 'flex', flexDirection: 'column-reverse', overflow: 'visible' }}
                                scrollableTarget="scrollableDiv"
                                inverse={true}
                                className={styles.msgsList}>
                                {groupedMessages?.map((message, index) => (
                                    <div
                                        key={message.id}
                                        className={
                                            message.type === 'date' ?
                                                undefined
                                            : (
                                                message.profile_id === userData?.id ?
                                                    styles.msgRowSentWrapper
                                                :
                                                    styles.msgRowAcceptedWrapper
                                            )
                                        }>
                                        {message.type === 'date' ? (
                                            <BadgeDay
                                                key={message.id}
                                                textDate={localizeDate(message.date)} />
                                        ) : (
                                            <ChatMessageItem
                                                key={message.id}
                                                id={message.id}
                                                status={message.is_read}
                                                datetime={new Date(message.created_at)}
                                                isMyMessage={message.profile_id === userData?.id}
                                                message={message.text}
                                                editedAt={message.edited_at ?? null}
                                                replyMsgId={message.reply_message_id ?? null}
                                                replyMsgContent={message.reply_message_text ?? null}
                                                hasReply={message.has_reply ?? false}
                                                unsafe_message={message.unsafe_message ?? false}
                                                profileChat={profileChat}
                                                ref={element => {
                                                    if (element)
                                                        msgRefs.current[message.id] = element
                                                    else
                                                        delete msgRefs.current[message.id]
                                                }}
                                                onMessageRead={sendMessageRead}
                                                onReplySwipe={handleReplySwipe} />
                                        )}
                                    </div>
                                ))}
                            </InfiniteScroll>
                        </>
                    ) : (
                        <div className={styles.emptyChatContainer}>
                            <div className={styles.emptyChatContainerMotivation}>
                                <div className={styles.emptyChatContainerMotivationIcon}>
                                    <ChatIcon />
                                </div>

                                <Typography
                                    variant="h4"
                                    center
                                    className={styles.emptyChatContainerMotivationTitle}>
                                    {
                                        profileChat ?
                                            t('msgs.empty_chat_placeholder_with_name', { name: profileChat.public_name })
                                        :
                                            t('msgs.empty_chat_placeholder')
                                    }
                                </Typography>
                                {
                                    isShowIcebreakerBtn &&
                                    <Typography
                                        variant="p"
                                        center
                                        className={styles.emptyChatContainerMotivationSubtitle}>
                                        {t('msgs.motivation_subtitle')}
                                    </Typography>
                                }
                                {
                                    isShowIcebreakerBtn ?
                                        <Button
                                            id="suggestion-btn"
                                            type="submit"
                                            disabled={isGenerateSuggestionBtnClicked}
                                            className={styles.suggestionsBtn}
                                            onClick={onHintWhatToWriteAboutClick}>
                                            {
                                                isSuggestionGenerated ?
                                                    t('msgs.another_idea')
                                                :
                                                    t('msgs.give_an_idea')
                                            }
                                        </Button>
                                    : <></>
                                }
                            </div>
                        </div>
                    )}
                </div>
            </Container>
            <FormMessage
                profileChat={profileChat}
                chatId={+chatId}
                isLoadingChat={isLoadingChat}
                isErrorChat={isErrorChat}
                expiredAt={expiredAt}
                onSend={sendMessage}
                onReply={replyMsg}
                onEdit={updateMsg}
                onFocus={onFocusHandle}
                onBlur={onBlurHandle}
                refetchMessages={refetchMessages}
                onChange={onMsgChanged}
                onSubmit={clearMsgCache}
                onChatModeClear={handleChatModeClear} />
        </div>
    );
};
